import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useFeatureFlag } from '@bonusly/who-am-i';

import { resolvePageContext } from '../lib/bizy-page-config';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';

interface UseBizyLayoutOptions {
	isMobile: boolean;
	isExpanded: boolean;
	setIsExpanded: (expanded: boolean) => void;
}

/**
 * Encapsulates all Bizy side-panel layout concerns so AppLayout stays clean.
 *
 * Handles: feature flag, store subscriptions, route-based page context,
 * homepage auto-open, and sidebar auto-collapse on medium screens.
 */
export function useBizyLayout({ isMobile, isExpanded, setIsExpanded }: UseBizyLayoutOptions) {
	const isEnabled = useFeatureFlag('habits-bu-beta');
	const location = useLocation();

	const isPanelOpen = useBizySidePanelStore((s) => s.isOpen);
	const panelWidth = useBizySidePanelStore((s) => s.width);
	const isResizing = useBizySidePanelStore((s) => s.isResizing);
	const togglePanel = useBizySidePanelStore((s) => s.toggle);
	const setDefaultOpen = useBizySidePanelStore((s) => s.setDefaultOpen);
	const setPageContext = useBizyPageContextStore((s) => s.setContext);

	const isDesktop = isEnabled && !isMobile;
	const isHomepage = location.pathname === '/';

	const [isLargeDesktop, setIsLargeDesktop] = useState(() => {
		if (typeof window === 'undefined') return false;
		return window.matchMedia('(min-width: 1280px)').matches;
	});

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const media = window.matchMedia('(min-width: 1280px)');
		const listener = (event: MediaQueryListEvent) => setIsLargeDesktop(event.matches);
		setIsLargeDesktop(media.matches);
		media.addEventListener('change', listener);

		return () => media.removeEventListener('change', listener);
	}, []);

	// Sync page context from current route
	useEffect(() => {
		if (!isEnabled || isMobile) return;
		setPageContext(resolvePageContext(location.pathname));
	}, [isEnabled, location.pathname, isMobile, setPageContext]);

	// Auto-open panel on homepage for large desktops (once per session)
	useEffect(() => {
		if (!isEnabled || isMobile || !isHomepage) return;
		setDefaultOpen(isLargeDesktop);
	}, [isEnabled, isHomepage, isLargeDesktop, isMobile, setDefaultOpen]);

	// Below lg: auto-collapse sidebar when Bizy opens, restore when it closes
	const wasExpandedBeforeBizyRef = useRef<boolean | null>(null);
	useEffect(() => {
		if (isLargeDesktop || isMobile) return;
		if (isPanelOpen) {
			if (isExpanded) {
				wasExpandedBeforeBizyRef.current = true;
				setIsExpanded(false);
			}
		} else if (wasExpandedBeforeBizyRef.current) {
			wasExpandedBeforeBizyRef.current = null;
			setIsExpanded(true);
		}
	}, [isPanelOpen, isLargeDesktop, isMobile]);

	return {
		isDesktop,
		isPanelOpen,
		panelWidth,
		isResizing,
		togglePanel,
	};
}
